/* Skeleton loading placeholders that mirror the real layouts. */

const Shimmer = ({ className = "" }) => (
  <div className={`relative overflow-hidden bg-earth-100/70 ${className}`}>
    <div className="absolute inset-0 -translate-x-full animate-skeleton-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent" />
  </div>
);

export function PublicSiteSkeleton() {
  return (
    <div data-testid="site-skeleton" className="min-h-screen bg-earth-50">
      {/* Nav placeholder */}
      <div className="fixed top-0 inset-x-0 z-50 py-5 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Shimmer className="h-10 w-28 rounded" />
          <div className="hidden md:flex items-center gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Shimmer key={i} className="h-3 w-16 rounded" />
            ))}
          </div>
          <Shimmer className="h-10 w-28 rounded-full" />
        </div>
      </div>

      {/* Hero placeholder */}
      <section className="relative h-screen min-h-[560px] overflow-hidden bg-earth-200/40">
        <Shimmer className="absolute inset-0" />
        <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-earth-50 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 h-full flex items-end pb-20">
          <div className="flex flex-col gap-4 max-w-2xl w-full">
            <Shimmer className="h-3 w-44 rounded" />
            <Shimmer className="h-12 w-3/4 rounded" />
            <Shimmer className="h-12 w-2/3 rounded" />
            <div className="flex flex-col gap-2 pt-3 max-w-md">
              <Shimmer className="h-3 w-full rounded" />
              <Shimmer className="h-3 w-5/6 rounded" />
            </div>
            <div className="flex gap-3 pt-4">
              <Shimmer className="h-12 w-40 rounded-full" />
              <Shimmer className="h-12 w-40 rounded-full" />
            </div>
          </div>
        </div>
      </section>

      {/* About row */}
      <section className="px-6 py-16">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 flex flex-col gap-4">
            <Shimmer className="h-3 w-28 rounded" />
            <Shimmer className="h-10 w-4/5 rounded" />
            <Shimmer className="h-10 w-3/5 rounded" />
            <div className="flex flex-col gap-2 pt-4">
              <Shimmer className="h-3 w-full rounded" />
              <Shimmer className="h-3 w-11/12 rounded" />
              <Shimmer className="h-3 w-10/12 rounded" />
            </div>
          </div>
          <div className="lg:col-span-5">
            <Shimmer className="aspect-[4/5] w-full rounded-3xl" />
          </div>
        </div>
      </section>

      {/* Amenities row */}
      <section className="px-6 py-12 bg-beige/20">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col gap-3 max-w-2xl">
            <Shimmer className="h-3 w-32 rounded" />
            <Shimmer className="h-10 w-4/5 rounded" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-8">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="rounded-3xl p-6 bg-white border border-earth-100 flex flex-col gap-4 min-h-[160px]"
              >
                <Shimmer className="w-14 h-14 rounded-2xl" />
                <Shimmer className="h-5 w-3/4 rounded" />
                <Shimmer className="h-3 w-1/3 rounded" />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export function BlogIndexSkeleton() {
  return (
    <div data-testid="blog-index-skeleton" className="min-h-screen bg-earth-50">
      {/* Nav placeholder */}
      <div className="fixed top-0 inset-x-0 z-50 bg-white/90 border-b border-earth-100/60 py-3 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Shimmer className="h-10 w-28 rounded" />
          <div className="hidden md:flex items-center gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Shimmer key={i} className="h-3 w-16 rounded" />
            ))}
          </div>
          <Shimmer className="h-10 w-28 rounded-full" />
        </div>
      </div>

      {/* Hero */}
      <section className="px-6 pt-32 pb-10 bg-gradient-to-b from-earth-900/90 to-earth-50">
        <div className="max-w-5xl mx-auto flex flex-col gap-4">
          <Shimmer className="h-3 w-24 rounded bg-white/20" />
          <Shimmer className="h-12 w-3/4 rounded bg-white/15" />
          <Shimmer className="h-12 w-1/2 rounded bg-white/15" />
        </div>
      </section>

      {/* Featured */}
      <section className="px-6 pb-12">
        <div className="max-w-7xl mx-auto">
          <div className="rounded-3xl overflow-hidden bg-white border border-earth-100 grid lg:grid-cols-12">
            <Shimmer className="lg:col-span-7 aspect-[16/10] lg:aspect-auto" />
            <div className="lg:col-span-5 p-10 flex flex-col gap-4 justify-center">
              <Shimmer className="h-3 w-36 rounded" />
              <Shimmer className="h-9 w-11/12 rounded" />
              <Shimmer className="h-9 w-9/12 rounded" />
              <div className="flex flex-col gap-2 pt-3">
                <Shimmer className="h-3 w-full rounded" />
                <Shimmer className="h-3 w-11/12 rounded" />
                <Shimmer className="h-3 w-9/12 rounded" />
              </div>
            </div>
          </div>

          {/* Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-3xl overflow-hidden border border-earth-100 flex flex-col"
              >
                <Shimmer className="aspect-[16/10]" />
                <div className="p-6 flex flex-col gap-3">
                  <Shimmer className="h-3 w-24 rounded" />
                  <Shimmer className="h-6 w-11/12 rounded" />
                  <Shimmer className="h-3 w-full rounded" />
                  <Shimmer className="h-3 w-10/12 rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export function BlogPostSkeleton() {
  return (
    <div data-testid="blog-post-skeleton" className="min-h-screen bg-earth-50">
      {/* Hero */}
      <section className="relative h-[60vh] sm:h-[70vh] min-h-[480px] overflow-hidden bg-earth-700">
        <Shimmer className="absolute inset-0" />
        <div className="absolute inset-0 bg-gradient-to-b from-earth-900/40 via-earth-900/50 to-earth-900/85" />
        {/* Nav placeholder */}
        <div className="absolute top-0 inset-x-0 z-10 py-5 px-6">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <Shimmer className="h-10 w-28 rounded bg-white/15" />
            <div className="hidden md:flex items-center gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <Shimmer key={i} className="h-3 w-16 rounded bg-white/15" />
              ))}
            </div>
            <Shimmer className="h-10 w-28 rounded-full bg-white/15" />
          </div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 pt-32 sm:pt-40 flex flex-col gap-4">
          <Shimmer className="h-3 w-44 rounded bg-white/20" />
          <Shimmer className="h-12 w-3/4 rounded bg-white/20" />
          <Shimmer className="h-12 w-1/2 rounded bg-white/20" />
        </div>
      </section>

      {/* Body */}
      <article className="px-6 py-14">
        <div className="max-w-3xl mx-auto flex flex-col gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Shimmer key={i} className={`h-3 rounded ${i % 4 === 3 ? "w-7/12" : "w-full"}`} />
          ))}
          <Shimmer className="h-6 w-1/2 rounded mt-6" />
          {Array.from({ length: 4 }).map((_, i) => (
            <Shimmer key={`b-${i}`} className={`h-3 rounded ${i === 3 ? "w-8/12" : "w-full"}`} />
          ))}
        </div>
      </article>
    </div>
  );
}
